import { useMemo } from "react";
import { Typography } from "@mui/material";
import { classifyBParty, classifyDirectionEvent } from "./cdrEngine";

export function buildTimelineData(filteredAnalysis, filteredNormRows, filteredInsights, topN) {
  const contacts = (filteredAnalysis?.mfcAll || []).slice(0, topN);
  if (!contacts.length) {
    return { rows: [], timeMin: null, timeMax: null, burstWindows: [], isEmpty: true };
  }

  const contactKeys = new Set(contacts.map((c) => c.bParty));
  const eventsByContact = new Map(contacts.map((c) => [c.bParty, []]));
  let timeMin = null;
  let timeMax = null;

  for (const r of filteredNormRows || []) {
    if (!(r.dateTime instanceof Date) || Number.isNaN(r.dateTime.getTime())) continue;
    const bInfo = classifyBParty(r.bParty);
    if (bInfo.type !== "MSISDN" || !contactKeys.has(bInfo.key)) continue;
    const { direction, event } = classifyDirectionEvent(r.callType || "");
    eventsByContact.get(bInfo.key).push({ dateTime: r.dateTime, event, direction });
    if (!timeMin || r.dateTime < timeMin) timeMin = r.dateTime;
    if (!timeMax || r.dateTime > timeMax) timeMax = r.dateTime;
  }

  const rows = contacts.map((c) => ({
    bParty: c.bParty,
    total: c.total,
    events: (eventsByContact.get(c.bParty) || []).slice().sort((a, b) => a.dateTime - b.dateTime),
  }));

  const burstWindows = (filteredInsights?.burstWindows || []).filter(
    (w) => timeMin && timeMax && w.end >= timeMin && w.start <= timeMax
  );

  return { rows, timeMin, timeMax, burstWindows, isEmpty: false };
}

export function timeScale(date, timeMin, timeMax, pixelWidth) {
  const span = timeMax.getTime() - timeMin.getTime();
  if (span <= 0) return 0;
  return ((date.getTime() - timeMin.getTime()) / span) * pixelWidth;
}

const CHART_WIDTH = 1000;
const ROW_HEIGHT = 40;
const HEADER_HEIGHT = 32;
const LABEL_WIDTH = 120;
const COLOR_BY_DIRECTION = { IN: "#16a34a", OUT: "#ea580c" };
const DEFAULT_COLOR = "#6b7280";

function buildTicks(timeMin, timeMax, count) {
  if (!timeMin || !timeMax) return [];
  const span = timeMax.getTime() - timeMin.getTime();
  const ticks = [];
  for (let i = 0; i < count; i += 1) {
    ticks.push(new Date(timeMin.getTime() + (span * i) / (count - 1)));
  }
  return ticks;
}

export default function CDRTimeline({ filteredAnalysis, filteredNormRows, filteredInsights, topN, onSelectContact }) {
  const data = useMemo(
    () => buildTimelineData(filteredAnalysis, filteredNormRows, filteredInsights, topN),
    [filteredAnalysis, filteredNormRows, filteredInsights, topN]
  );

  if (data.isEmpty) {
    return (
      <Typography variant="body2" color="text.secondary">
        No contacts to graph (check filters).
      </Typography>
    );
  }

  const { rows, timeMin, timeMax, burstWindows } = data;
  const ticks = buildTicks(timeMin, timeMax, 6);
  const totalHeight = HEADER_HEIGHT + rows.length * ROW_HEIGHT;
  const x = (d) => timeScale(d, timeMin, timeMax, CHART_WIDTH);

  return (
    <div style={{ display: "flex" }}>
      <svg width={LABEL_WIDTH} height={totalHeight} style={{ flexShrink: 0 }}>
        {rows.map((row, i) => (
          <text key={row.bParty} x={4} y={HEADER_HEIGHT + i * ROW_HEIGHT + ROW_HEIGHT / 2 + 4} fontSize={12}>
            {row.bParty}
          </text>
        ))}
      </svg>
      <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
        <svg width={CHART_WIDTH} height={totalHeight}>
          {ticks.map((t, i) => (
            <g key={i}>
              <line x1={x(t)} y1={0} x2={x(t)} y2={totalHeight} stroke="#e5e7eb" />
              <text x={x(t)} y={20} fontSize={11}>
                {t.toLocaleDateString()}
              </text>
            </g>
          ))}
          {burstWindows.map((w, i) => (
            <rect
              key={i}
              data-testid="burst-window"
              x={x(w.start)}
              y={HEADER_HEIGHT}
              width={Math.max(2, x(w.end) - x(w.start))}
              height={rows.length * ROW_HEIGHT}
              fill="#fde68a"
              opacity={0.4}
            />
          ))}
          {rows.map((row, rowIndex) =>
            row.events.map((ev, evIndex) => {
              const cx = x(ev.dateTime);
              const cy = HEADER_HEIGHT + rowIndex * ROW_HEIGHT + ROW_HEIGHT / 2;
              const fill = COLOR_BY_DIRECTION[ev.direction] || DEFAULT_COLOR;
              const title = `${ev.direction || "-"} ${ev.event} — ${ev.dateTime.toLocaleString()}`;
              const handleClick = () => onSelectContact && onSelectContact(row.bParty);
              const key = `${row.bParty}-${evIndex}`;
              if (ev.event === "SMS") {
                return (
                  <rect
                    key={key}
                    data-testid="timeline-event"
                    x={cx - 4}
                    y={cy - 4}
                    width={8}
                    height={8}
                    fill={fill}
                    onClick={handleClick}
                    style={{ cursor: "pointer" }}
                  >
                    <title>{title}</title>
                  </rect>
                );
              }
              return (
                <circle
                  key={key}
                  data-testid="timeline-event"
                  cx={cx}
                  cy={cy}
                  r={4}
                  fill={fill}
                  onClick={handleClick}
                  style={{ cursor: "pointer" }}
                >
                  <title>{title}</title>
                </circle>
              );
            })
          )}
        </svg>
      </div>
    </div>
  );
}
